import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsTicketdefV3TicketDefinitionManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events-ticket-definitions',
        destPath: '',
      },
      {
        srcPath: '/events/v3/ticket-definitions',
        destPath: '/v3/ticket-definitions',
      },
      {
        srcPath: '/events/v3/bulk/ticket-definitions',
        destPath: '/v3/bulk/ticket-definitions',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/events-ticket-definitions',
        destPath: '',
      },
    ],
    'events.wixapps.net': [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-ticket-definitions',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_ticket-definitions-v-2';

/**
 * Creates a ticket definition.
 *
 * You may create up to 100 definitions per event.
 */
export function createTicketDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __createTicketDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'ticketDefinition.createdDate' },
          { path: 'ticketDefinition.updatedDate' },
          { path: 'ticketDefinition.eventCreatedDate' },
          { path: 'ticketDefinition.salePeriod.startDate' },
          { path: 'ticketDefinition.salePeriod.endDate' },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
          },
          { path: 'ticketDefinition.eventDetails.dateAndTimeSettings.endDate' },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
          },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'ticketDefinition.eventDetails.location.address.geocode.latitude',
          },
          {
            path: 'ticketDefinition.eventDetails.location.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.CreateTicketDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketDefinition.createdDate' },
              { path: 'ticketDefinition.updatedDate' },
              { path: 'ticketDefinition.eventCreatedDate' },
              { path: 'ticketDefinition.salePeriod.startDate' },
              { path: 'ticketDefinition.salePeriod.endDate' },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.endDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.latitude',
              },
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createTicketDefinition;
}

/**
 * Updates a ticket definition.
 *
 * Each time the ticket definition is updated, `revision` increments by 1. The existing `revision` must be included when updating the ticket definition. This ensures you're working with the latest ticket definition and prevents unintended overwrites.
 */
export function updateTicketDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __updateTicketDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'ticketDefinition.createdDate' },
          { path: 'ticketDefinition.updatedDate' },
          { path: 'ticketDefinition.eventCreatedDate' },
          { path: 'ticketDefinition.salePeriod.startDate' },
          { path: 'ticketDefinition.salePeriod.endDate' },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
          },
          { path: 'ticketDefinition.eventDetails.dateAndTimeSettings.endDate' },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
          },
          {
            path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'ticketDefinition.eventDetails.location.address.geocode.latitude',
          },
          {
            path: 'ticketDefinition.eventDetails.location.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'PATCH' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.UpdateTicketDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/{ticketDefinition.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketDefinition.createdDate' },
              { path: 'ticketDefinition.updatedDate' },
              { path: 'ticketDefinition.eventCreatedDate' },
              { path: 'ticketDefinition.salePeriod.startDate' },
              { path: 'ticketDefinition.salePeriod.endDate' },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.endDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.latitude',
              },
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateTicketDefinition;
}

/** Retrieves a ticket definition. */
export function getTicketDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __getTicketDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'GET' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.GetTicketDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/{ticketDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketDefinition.createdDate' },
              { path: 'ticketDefinition.updatedDate' },
              { path: 'ticketDefinition.eventCreatedDate' },
              { path: 'ticketDefinition.salePeriod.startDate' },
              { path: 'ticketDefinition.salePeriod.endDate' },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.endDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'ticketDefinition.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.latitude',
              },
              {
                path: 'ticketDefinition.eventDetails.location.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTicketDefinition;
}

/** Deletes a ticket definition. */
export function deleteTicketDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteTicketDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'DELETE' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.DeleteTicketDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/{ticketDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteTicketDefinition;
}

/**
 * Adjusts the order in which ticket definitions display in an event dashboard, as well as the order of available tickets displayed in the ticket picker.
 * > **Note:** You may use both `beforeTicketDefinitionId` and `afterTicketDefinitionId` but only the last one provided takes effect.
 */
export function reorderTicketDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __reorderTicketDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.ReorderTicketDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/reorder',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __reorderTicketDefinitions;
}

/**
 * Retrieves a list of ticket definitions, given the provided paging, filtering, and sorting.
 * Query Ticket Definitions runs with these defaults, which you can override:
 * - `createdDate` is sorted in `ASC` order
 * - `paging.limit` is `100`
 * - `paging.offset` is `0`
 */
export function queryTicketDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryTicketDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.QueryTicketDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketDefinitions.createdDate' },
              { path: 'ticketDefinitions.updatedDate' },
              { path: 'ticketDefinitions.eventCreatedDate' },
              { path: 'ticketDefinitions.salePeriod.startDate' },
              { path: 'ticketDefinitions.salePeriod.endDate' },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.startDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.endDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'ticketDefinitions.eventDetails.location.address.geocode.latitude',
              },
              {
                path: 'ticketDefinitions.eventDetails.location.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryTicketDefinitions;
}

/**
 * Retrieves a list of available ticket definitions with the specified paging, filtering, and sorting.
 *
 * This method retrieves ticket definitions that aren't in the `hidden` state. The available ticket definitions can be retrieved by site visitors.
 *
 * > **Note:** You need the `WIX_EVENTS.READ_TICKET_DEFINITIONS` permission to get the `salesDetails` field values.
 *
 * Query Available Ticket Definitions runs with these defaults, which you can override:
 * - `createdDate` is sorted in `ASC` order
 * - `paging.limit` is `100`
 * - `paging.offset` is `0`
 *
 * To learn about working with _Query_ methods, see [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language).
 */
export function queryAvailableTicketDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAvailableTicketDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.QueryAvailableTicketDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/available/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ticketDefinitions.createdDate' },
              { path: 'ticketDefinitions.updatedDate' },
              { path: 'ticketDefinitions.eventCreatedDate' },
              { path: 'ticketDefinitions.salePeriod.startDate' },
              { path: 'ticketDefinitions.salePeriod.endDate' },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.startDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.endDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'ticketDefinitions.eventDetails.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'ticketDefinitions.eventDetails.location.address.geocode.latitude',
              },
              {
                path: 'ticketDefinitions.eventDetails.location.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAvailableTicketDefinitions;
}

/** Counts ticket definitions by the `saleStatus` field, including those with the `hidden` status. */
export function countTicketDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __countTicketDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.CountTicketDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countTicketDefinitions;
}

/** Counts ticket definitions by the `saleStatus`, excluding those with the `hidden` status. */
export function countAvailableTicketDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __countAvailableTicketDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.CountAvailableTicketDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/available/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countAvailableTicketDefinitions;
}

/**
 * Deletes multiple ticket definitions.
 *
 * All ticket definitions that meet the specified `filter` criteria are deleted.
 */
export function bulkDeleteTicketDefinitionsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteTicketDefinitionsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.BulkDeleteTicketDefinitionsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/bulk/ticket-definitions/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteTicketDefinitionsByFilter;
}

/**
 * Changes ticket price currency per event.
 *
 * Updates the currency for all ticket definitions under the event.
 * This doesn't convert price values. It only updates the currency code
 * (for example, 10 USD becomes 10 EUR).
 */
export function changeCurrency(payload: object): RequestOptionsFactory<any> {
  function __changeCurrency({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.ticket_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.events.ticketdef.v3.TicketDefinitionManagement.ChangeCurrency',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsTicketdefV3TicketDefinitionManagementUrl({
        protoPath: '/v3/ticket-definitions/currency',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changeCurrency;
}
